import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/site/Logo";
import { formatApiError } from "@/lib/api";
import { EASE } from "@/components/motion/Reveal";

const GoogleIcon = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.8-5.5 3.8-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.9 1.5l2.6-2.6C16.9 3.1 14.7 2 12 2 6.5 2 2 6.5 2 12s4.5 10 10 10c5.8 0 9.6-4.1 9.6-9.8 0-.7-.1-1.2-.2-1.7H12z" />
  </svg>
);

export default function AdminLogin() {
  const { user, login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { document.title = "Admin Login — West Canal Motors"; }, []);

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") {
        setError("This account does not have admin access.");
      } else {
        toast.success(`Welcome back, ${u.name || "Admin"}.`);
        navigate("/admin", { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-obsidian text-white lg:grid-cols-2" data-testid="admin-login-page">
      <div className="relative hidden overflow-hidden border-r border-white/10 lg:block">
        <div className="grid-texture absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
        <div className="spotlight absolute inset-0" />
        <p className="outline-text absolute -left-6 top-10 font-display text-[16vw] font-black leading-none">ADMIN</p>
        <motion.img initial={{ x: 80, opacity: 0, filter: "blur(10px)" }} animate={{ x: 0, opacity: 1, filter: "blur(0px)" }} transition={{ duration: 1.2, ease: EASE }} src="/vehicles/t2-idm/carbon-black.png" alt="" className="absolute bottom-[18%] left-[-6%] w-[110%] drop-shadow-[0_40px_60px_rgba(0,0,0,0.8)]" />
        <div className="absolute bottom-12 left-12">
          <p className="eyebrow">West Canal Motors</p>
          <p className="mt-2 font-display text-3xl font-bold">Dealership Control Panel</p>
          <p className="mt-2 max-w-sm text-sm text-slate-400">Manage models, pricing, leads, gallery and site content.</p>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 py-16 sm:px-16 lg:px-24">
        <Logo subtitle="Admin" />
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.1 }} className="mt-14 max-w-md">
          <p className="eyebrow">Sign in</p>
          <h1 className="mt-3 font-display text-4xl font-bold tracking-tight">Welcome back.</h1>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="admin-login-form">
            <div>
              <label className="field-label" htmlFor="admin-email">Email</label>
              <input id="admin-email" type="email" autoComplete="email" data-testid="admin-login-email-input" className="field" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <label className="field-label" htmlFor="admin-password">Password</label>
              <input id="admin-password" type="password" autoComplete="current-password" data-testid="admin-login-password-input" className="field" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            {error && <p data-testid="admin-login-error" className="rounded-md border border-crimson/40 bg-crimson/10 px-4 py-3 text-sm text-crimson">{error}</p>}
            <button type="submit" disabled={loading} data-testid="admin-login-submit-button" className="btn-crimson w-full !py-4 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          <div className="my-8 flex items-center gap-4"><span className="h-px flex-1 bg-white/10" /><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">or</span><span className="h-px flex-1 bg-white/10" /></div>

          <button type="button" onClick={loginWithGoogle} data-testid="admin-google-login-button" className="flex w-full items-center justify-center gap-3 rounded-full border border-white/15 py-4 font-display text-sm font-semibold transition-colors hover:border-white/50 hover:bg-white/5">
            <GoogleIcon className="h-5 w-5" /> Continue with Google
          </button>
          <p className="mt-3 text-center text-xs text-slate-500">Google sign-in works only for whitelisted dealership accounts.</p>
        </motion.div>
      </div>
    </div>
  );
}
